import { ActivityCard } from '@src/components/ActivityCard';
import {
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Card,
  ExecutionTimelineFilters,
  Flex,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  MixpanelEvent,
  NavButton,
  Portal,
  SectionTitle,
  Text,
  Toggle,
  Tour,
  isCalendarSpread,
  isEntityRFQ,
  isMultileg,
  orderHasLegSummary,
  useMixpanel,
  useSecurity,
  type CustomerOrder,
  type Order,
} from '@talos/kyoko';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { MultilegTable } from '../../../../../components/Multileg';
import { useFeatureFlag } from '../../../../../hooks';
import { useCurrentAppLayoutId } from '../../../../../hooks/useAppLayoutId';
import { TourName } from '../../../../../tokens/tours';
import { getTradingOrderDetailsRoute } from '../../../../Routes/routes';
import { useOrderDetailsRoute } from '../routingUtils/useOrderDetailsRoute';
import { TopBarUtils } from '../TopBarUtils';
import type { OrderDetailsPath } from '../types';
import { ContractDetailsCard } from './ContractDetailsCard';
import { DDHHedgeOrderDetails } from './DDH/DDHHedgeOrderDetails/DDHHedgeOrderDetails';
import { DDHParentOrderDetails } from './DDH/DDHParentOrderDetails/DDHParentOrderDetails';
import { FilledCard } from './FilledCard';
import { GeneralCard } from './GeneralCard';
import { OrderCard } from './OrderCard';
import { OtherCard } from './OtherCard';
import { ParametersCard } from './ParametersCard';
import { RFQCard } from './RFQCard';
import { CustomGridLayout, DetailsWrapper, GridItem } from './styles';

export interface DetailsTabProps {
  order: Order;
  customerOrder?: CustomerOrder;
  width: number;
}

export function DetailsTab({ order, customerOrder, width }: DetailsTabProps) {
  const mixpanel = useMixpanel();
  const { spacing4 } = useTheme();
  const [showExpandedDetails, setShowExpandedDetails] = useState(false);
  const onChangeExpandedDetails = () => {
    mixpanel.track(MixpanelEvent.ShowExpandedDetails);
    return setShowExpandedDetails(show => !show);
  };
  const security = useSecurity(order.Symbol);
  const isOrderMultiLeg = orderHasLegSummary(order) && (isMultileg(security) || isCalendarSpread(security));
  const [timelineFilters, setTimelineFilters] = useState<string[]>([]);
  const gridSize = width > 1600 ? 'lg' : width > 1200 ? 'md' : 'sm';
  const { enableDynamicDeltaHedging } = useFeatureFlag();
  const urlUpToOrderId = useOrderDetailsRoute();
  const layoutId = useCurrentAppLayoutId();

  const isDDH = enableDynamicDeltaHedging && (order.isDDHHedgeOrder || order.isDDHParentOrder);

  return (
    <>
      <Portal portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}>
        <HStack gap="spacing8">
          <Toggle
            size={FormControlSizes.Small}
            onChange={onChangeExpandedDetails}
            label="Show expanded details"
            checked={showExpandedDetails}
          />
          <TopBarUtils order={order} customerOrder={customerOrder} latestAnalytics={null} />
        </HStack>
      </Portal>
      <DetailsWrapper data-testid="order-details-tab">
        <CustomGridLayout size={gridSize} isOrderMultiLeg={isOrderMultiLeg} isDDHEnabled={isDDH}>
          {security && isOrderMultiLeg && (
            <Flex gridArea="multileg">
              <MultilegTable order={order} orderSecurity={security} />
            </Flex>
          )}
          {enableDynamicDeltaHedging && (
            <>
              {order.isDDHParentOrder && (
                /* Show the section titles and render the DDHParentOrderDetails component */
                <Flex flexDirection="column" gridArea="ddh" gap="spacing8">
                  <Tour tour={TourName.v249DDHOrderDetailsViewHedgeOrder} />
                  <SectionTitle
                    title="DDH Summary"
                    suffix={
                      <NavButton
                        size={FormControlSizes.Tiny}
                        to={`${urlUpToOrderId}/${'ddh-orders' satisfies OrderDetailsPath}`}
                        onClick={() => mixpanel.track(MixpanelEvent.ViewAllDDHOrders)}
                        endIcon={IconName.ChevronRight}
                        dim={true}
                        data-testid="view-ddh-orders"
                      >
                        View All DDH Orders
                      </NavButton>
                    }
                  />
                  <DDHParentOrderDetails order={order} />
                  <SectionTitle title="Order Summary" />
                </Flex>
              )}
              {order.isDDHHedgeOrder && order.ddhParentOrderID && (
                /* Show the section titles and render the DDHHedgeOrderDetails component */
                <Flex flexDirection="column" gridArea="ddh" gap="spacing8">
                  <Tour tour={TourName.v249DDHOrderDetailsViewParentOrder} />
                  <SectionTitle
                    title="Parent Order Summary"
                    suffix={
                      <NavButton
                        size={FormControlSizes.Tiny}
                        to={getTradingOrderDetailsRoute({ orderID: order.ddhParentOrderID, type: 'order', layoutId })}
                        onClick={() => mixpanel.track(MixpanelEvent.ViewParentOrderDetails)}
                      >
                        <HStack
                          data-testid="view-ddh-parent-order"
                          gap="spacing2"
                          color="colorTextSubtle"
                          fontWeight="bold"
                        >
                          <Text>View Parent Order Details</Text>
                          <Icon icon={IconName.ChevronRight} />
                        </HStack>
                      </NavButton>
                    }
                  />
                  <DDHHedgeOrderDetails hedgeOrder={order} />
                  <SectionTitle title="DDH Order Summary" />
                </Flex>
              )}
            </>
          )}
          <GridItem gridArea="timeline" overflow="hidden">
            <Card
              display="flex"
              flexDirection="column"
              alignItems="stretch"
              minHeight="200px"
              pb={0}
              h="100%"
              title="Timeline"
              actions={<ExecutionTimelineFilters filters={timelineFilters} setTimelineFilters={setTimelineFilters} />}
            >
              <ActivityCard
                entity={customerOrder ?? order}
                statuses={timelineFilters}
                w={`calc(100% + ${spacing4}px)`}
                mx="-spacing4"
              />
            </Card>
          </GridItem>
          <GridItem gridArea="order">
            <OrderCard
              order={order}
              customerOrder={customerOrder}
              showExpandedDetails={showExpandedDetails}
              data-testid="ord-details-order-card"
            />
            <FilledCard
              order={order}
              customerOrder={customerOrder}
              showExpandedDetails={showExpandedDetails}
              data-testid="order-details-filled-card"
            />
            <ContractDetailsCard order={order} showExpandedDetails={showExpandedDetails} />
          </GridItem>
          <GridItem gridArea="general">
            <GeneralCard order={order} customerOrder={customerOrder} showExpandedDetails={showExpandedDetails} />
            {isEntityRFQ(order) ? (
              <RFQCard order={order} />
            ) : (
              <ParametersCard showExpandedDetails={showExpandedDetails} order={order} />
            )}
            <OtherCard order={order} showExpandedDetails={showExpandedDetails} />
          </GridItem>
        </CustomGridLayout>
      </DetailsWrapper>
    </>
  );
}
